import { Dispatch, Draft } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import axiosRetry, { IAxiosRetryConfig } from 'axios-retry';
import getStore from 'store/store';
import * as GraphQlMeasurements from 'store/api/measurements/graphQlMeasurements';
import { getPerformance } from 'core/window/windowService';
import { ActivityGqlConfiguration } from 'store/api/ActivityGqlConfiguration';
import {
    ActionCreatorWithGqlApiResponseType,
    ActivitiesGqlResponseType,
    ActivityGqlConfigurationValueUnionType,
    ActivityGqlOperation,
    ActivityGqlQueryArgsType,
    ApiBaseState,
    GenericGqlResponse,
    GqlApiError,
    GqlApiErrorWithResponseInfo,
    GqlBodyProps,
    QueryArgType,
    UseQueryStateBaseResultWithoutError,
} from 'store/api/api.types';
import { buildGraphQlHttpHeaders } from 'store/api/graphQlHeaders';
import { NavigationState } from 'store/slice';
import { BootstrapState } from 'store/slice/bootstrap';
import { UnionToIntersection } from '@reduxjs/toolkit/src/query/tsHelpers';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { getGqlApiErrorFromAxiosError, getResponseError, GqlHeaders, logGql } from 'store/api/apiUtils';
import { ActivitiesPageTypes } from 'core/constant/analytics/AnalyticsPageType';
import { DefaultApiBaseState } from 'store/api/ApiConstants';

declare module 'axios' {
    // In case you need a new property to be passed to the callbacks, add the property/field here.
    export interface AxiosRequestConfig {
        operation?: ActivityGqlOperation;
        pageTypeId?: number;
        isBot?: boolean;
    }
}

export const GqlApiBaseStateLoading = { ...DefaultApiBaseState, isLoading: true };
export const GqlApiBaseStateSuccess = { ...DefaultApiBaseState, isSuccess: true };

interface CommonResponsePropsType {
    pageTypeId: ActivitiesPageTypes;
    config: BootstrapConfig;
    props: UnionToIntersection<ActivityGqlQueryArgsType>;
    state: { navigation: NavigationState; bootstrap: BootstrapState };
    result: {};
}

type QueryArgTypeArgs = UnionToIntersection<QueryArgType['args']>;
type ActivityGqlConfigurationValueTypeArgs = UnionToIntersection<Parameters<ActivityGqlConfigurationValueUnionType['buildRequestPayload']>[0]>;
type AxiosGqlContextType = QueryArgType & {
    abortController: AbortController;
    commonResponseProps: CommonResponsePropsType;
    config: BootstrapConfig;
    dispatch: Dispatch;
    pageTypeId: ActivitiesPageTypes;
    requestKey: string;
    state: { navigation: NavigationState; bootstrap: BootstrapState };
    targetAction: ActivityGqlConfigurationValueUnionType;
    buildUrl: (queryString: string) => string;
    cleanupActiveRequestCache: () => void;
    setResponseInState?: ActionCreatorWithGqlApiResponseType;
};

interface GqlQueryResultType {
    promise: Promise<GqlApiError | GenericGqlResponse | undefined>;
    abortController: AbortController;
}

const ActiveRequests: Record<string, AbortController | undefined> = {};

export const resetStateWithDefaultApiBaseState = <T extends ApiBaseState>(state: Draft<T>): Draft<T> => {
    state.isSuccess = DefaultApiBaseState.isSuccess;
    state.isLoading = DefaultApiBaseState.isLoading;
    state.isError = DefaultApiBaseState.isError;
    state.error = DefaultApiBaseState.error;
    return state;
};

export const isDefaultGqlState = (state: ApiBaseState): boolean => {
    return (
        state.isSuccess === DefaultApiBaseState.isSuccess &&
        state.isLoading === DefaultApiBaseState.isLoading &&
        state.isError === DefaultApiBaseState.isError &&
        state.error === DefaultApiBaseState.error
    );
};

export const isApiCallCompleted = (result: UseQueryStateBaseResultWithoutError<{}>): boolean => {
    if (!result) return false;
    return !result.isUninitialized && (result.isError || result.isSuccess);
};

// The retry intervals are mutually agreed with the supply team.
// Please do not modify these numbers without discussing with the supply team.
// Rationale
// 0: (initial call) it is called immediately
// 1: 1st retry, We want to keep it small. The API will respond within 500ms in case the response exists in cache.
// 2: 1st retry, We need to allow backend sometime (7-10s) to populate the cache.
//     We might want to tune the delay for the second retry.
//     Hypothesis: It would be a better experience to show a delayed response than a technical error.
const RetryInterval: Record<number, number> = { 0: 1000, 1: 1000, 2: 4000, 3: 4000 };

/**
 * attempt: attempt parameter starts from index 1
 */
function customExponentialBackoff(attempt = 0) {
    // The fallback case of 2s delay should never happen.
    return RetryInterval[attempt] || 2000;
}

const axiosInstance = axios.create();

export const sendMeasurementAndLogGqlErrorWithExtraInfo = (bootstrap: BootstrapConfig, message: string, pageTypeId: ActivitiesPageTypes, error: GqlApiErrorWithResponseInfo) => {
    switch (message.toLowerCase()) {
        case 'failed to fetch':
            GraphQlMeasurements.countNetworkError(error.operation, pageTypeId);
            logGql(bootstrap, error, pageTypeId, 'error');
            break;
        case 'incomplete response':
            GraphQlMeasurements.countGraphQLIncomplete(error.operation, pageTypeId);
            logGql(bootstrap, error, pageTypeId, 'warn');
            break;
        default:
            message && logGql(bootstrap, error, pageTypeId, message.includes('ASAPIErrorType') ? 'warn' : 'error');
            break;
    }
};

const shouldRetry = (err: AxiosError) => {
    const error = err as AxiosError<ActivitiesGqlResponseType>;
    const { response, config } = error;
    const status = response?.status ?? 0;
    // 429 Too Many Requests
    if (!config || config.isBot === true || status === 401 || status === 429 || status >= 500) {
        return false;
    }
    if (response?.data?.data) {
        const operation = config.operation;
        const responseError = getResponseError(response, operation);
        return responseError.shouldRetry;
    }
    return axiosRetry.isNetworkOrIdempotentRequestError(error);
};

const buildRequestPayload = (axiosGqlContext: AxiosGqlContextType) => {
    const { args, operation, targetAction, dispatch, onError, setResponseInState } = axiosGqlContext;
    const payload = targetAction.buildRequestPayload(args as ActivityGqlConfigurationValueTypeArgs);
    const statusErrorResult = targetAction.validateRequestPayload(payload);

    if (statusErrorResult) {
        const errorData = statusErrorResult.error.data;
        const apiError: GqlApiError = {
            ...statusErrorResult.error,
            operation,
            status: 400,
            data: JSON.stringify(errorData),
            errorCode: errorData?.errorCode,
            subErrorCode: errorData?.subErrorCode,
            message: errorData?.message,
            name: errorData?.errorCode,
            isUnhandledError: false,
        };
        // @ts-ignore
        setResponseInState && dispatch(setResponseInState({ ...axiosGqlContext.commonResponseProps, baseState: { ...DefaultApiBaseState, isError: true, error: apiError } }));
        onError && onError(operation, args as QueryArgTypeArgs, apiError, undefined);
        return undefined;
    }
    return payload;
};

const handleError = (error: AxiosError<ActivitiesGqlResponseType>, gqlContext: AxiosGqlContextType) => {
    const { args, operation, pageTypeId, dispatch, onError, setResponseInState } = gqlContext;
    if (error.code === AxiosError.ERR_CANCELED) {
        // for cancelled requests, do not log errors and keep loading state
        onError && onError(operation, args as QueryArgTypeArgs, undefined, error);
        return undefined;
    }

    gqlContext.cleanupActiveRequestCache(); // Clear the AbortController only after exhausting the retries

    const apiError = getGqlApiErrorFromAxiosError(error, operation);
    if (apiError.status > 0 && apiError.status !== 206) {
        GraphQlMeasurements.countGraphQLError(operation, pageTypeId, apiError.status, apiError.errorCode, apiError.subErrorCode);
    }

    // @ts-ignore
    setResponseInState && dispatch(setResponseInState({ ...gqlContext.commonResponseProps, baseState: { ...DefaultApiBaseState, isError: true, error: apiError } }));
    sendMeasurementAndLogGqlErrorWithExtraInfo(gqlContext.config, apiError.message, pageTypeId, apiError);

    // @ts-ignore
    onError && onError(operation, args, apiError, error);
    return apiError;
};

const getAxiosRetryConfig = () => {
    // Please do not read any value from Closures. These callbacks suffer from the stale closure problem.

    const internalRetryInterceptorId = axiosInstance.interceptors.response.use((response) => {
        // We must throw error for incomplete response to trigger the retry mechanism.
        // The retry mechanism does not trigger if we throw error from the "then" method.
        const responseError = getResponseError(response, response.config.operation);
        if (responseError && responseError.message) {
            const errorCode = responseError.shouldRetry ? AxiosError.ERR_NETWORK : AxiosError.ERR_BAD_RESPONSE;
            throw new AxiosError(responseError.message, errorCode, response.config, response.request, response);
        }
        return response;
    });

    // The retry interceptor must be initialized after 'retryInterceptorId'. We need to retry the request when "response.result.isCompleted === false"
    const { requestInterceptorId, responseInterceptorId } = axiosRetry(axiosInstance<GqlBodyProps>, {
        retries: 3,
        retryDelay: customExponentialBackoff,
        retryCondition: shouldRetry,
    });

    const axiosRetryConfig: IAxiosRetryConfig = {
        retries: 2, // Total 3 requests, 1 initial + 2 retries
        onRetry: (retryCount, _error, requestConfig) => {
            if (retryCount > 0) {
                GraphQlMeasurements.countRetry(requestConfig.operation || '', requestConfig.pageTypeId || 0, retryCount);
            } else {
                GraphQlMeasurements.countTotal(requestConfig.operation || '', requestConfig.pageTypeId || 0);
            }
            if (requestConfig.headers) {
                requestConfig.headers[GqlHeaders.RetryAttempt] = retryCount.toString();
                requestConfig.headers[GqlHeaders.RequestAttempt] = (retryCount + 1).toString();
            }
        },
    };

    return {
        axiosRetryConfig,
        interceptorIds: { request: [requestInterceptorId], response: [internalRetryInterceptorId, responseInterceptorId] },
    };
};

export const getAxiosGqlContext = (queryArgs: QueryArgType): AxiosGqlContextType => {
    const store = getStore();
    const { operation, key } = queryArgs;
    const targetAction = ActivityGqlConfiguration[operation];
    const state = store.getState() as unknown as { navigation: NavigationState; bootstrap: BootstrapState };
    const requestKey = key ? `${operation}-${key}` : operation;
    const previousRequest = ActiveRequests[requestKey];
    const isCustomStateHandling = queryArgs.args.customStateHandling;

    if (previousRequest && !!isCustomStateHandling) {
        previousRequest.abort();
        ActiveRequests[requestKey] = undefined;
    }
    const abortController = new AbortController();
    ActiveRequests[requestKey] = abortController;

    return {
        ...queryArgs,
        abortController,
        targetAction,
        requestKey,
        state,
        dispatch: store.dispatch,
        commonResponseProps: {
            state,
            result: {},
            config: state.bootstrap.config,
            pageTypeId: state.navigation.pageTypeId,
            props: queryArgs.args.props as UnionToIntersection<ActivityGqlQueryArgsType>,
        },
        config: state.bootstrap.config,
        pageTypeId: state.navigation.pageTypeId,
        setResponseInState: !isCustomStateHandling ? targetAction.setResponseInStateFn() : undefined,
        buildUrl: (queryString: string) => `${state.bootstrap.config.webgateUrl}/graphql${queryString}`,
        cleanupActiveRequestCache: () => {
            ActiveRequests[requestKey] = undefined;
        },
    };
};

const queryActivitiesGqlApi = (queryArgs: QueryArgType): GqlQueryResultType | undefined => {
    const gqlHeaders = buildGraphQlHttpHeaders();
    const axiosGqlContext = getAxiosGqlContext(queryArgs);
    const { abortController, operation, targetAction, dispatch, setResponseInState } = axiosGqlContext;

    // @ts-ignore
    setResponseInState && dispatch(setResponseInState({ ...axiosGqlContext.commonResponseProps, baseState: GqlApiBaseStateLoading }));

    const payload = buildRequestPayload(axiosGqlContext);
    if (!payload) {
        return undefined;
    }

    const { axiosRetryConfig, interceptorIds } = getAxiosRetryConfig();

    // Append the key to the queryString here to distinguish between each request with the same operation for playwright tests.
    const queryString = queryArgs.key ? `${payload.queryString}&key=${queryArgs.key}` : payload.queryString;
    const start = getPerformance().now();
    const promise = axiosInstance
        .post<ActivitiesGqlResponseType>(axiosGqlContext.buildUrl(queryString), payload, {
            operation,
            pageTypeId: axiosGqlContext.pageTypeId,
            isBot: axiosGqlContext.config.isBot,
            headers: gqlHeaders,
            signal: abortController.signal,
            'axios-retry': axiosRetryConfig,
        })
        .then((response) => {
            axiosGqlContext.cleanupActiveRequestCache();
            if (!response || !response.data || !response.data.data) {
                throw new AxiosError(response.statusText, AxiosError.ERR_BAD_RESPONSE, response.config, response.request, response);
            }

            const result = targetAction.handleResponse(response.data);
            // @ts-ignore
            setResponseInState && dispatch(setResponseInState({ ...axiosGqlContext.commonResponseProps, result, baseState: GqlApiBaseStateSuccess }));
            const roundTripTime = getPerformance().now() - start;
            GraphQlMeasurements.measureRoundTripTime(operation, roundTripTime, axiosGqlContext.pageTypeId);
            queryArgs.onSuccess && queryArgs.onSuccess(operation, axiosGqlContext.args as QueryArgTypeArgs, result);
            return result;
        })
        .catch((e) => {
            return handleError(e as AxiosError<ActivitiesGqlResponseType>, axiosGqlContext);
        })
        .finally(() => {
            // Cleanup interceptions to avoid memory leaks and to avoid running extra interceptors on request.
            interceptorIds.request.forEach((interceptorId) => axiosInstance.interceptors.request.eject(interceptorId));
            interceptorIds.response.forEach((interceptorId) => axiosInstance.interceptors.response.eject(interceptorId));
        });

    return { promise, abortController };
};

export default queryActivitiesGqlApi;
